import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extracts an error message from an unknown error value.
 *
 * Handles common error formats:
 * - Error instances (error.message)
 * - Strings (returned as-is)
 * - Objects with message property (e.g., Supabase errors, fetch responses)
 *
 * @param error - The error to extract a message from
 * @param fallback - Fallback message if error format is unrecognized (default: 'An unexpected error occurred')
 * @returns The error message string
 *
 * @example
 * ```ts
 * try {
 *   await riskyOperation();
 * } catch (err) {
 *   const message = getErrorMessage(err, 'Operation failed');
 *   toast.error(message);
 * }
 * ```
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  // Handle objects with a message property (e.g., Supabase errors, API responses)
  if (
    error !== null &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
}

/**
 * Normalizes a caught `unknown` value into a typed `Error`.
 *
 * Real `Error` instances (including subclasses) are returned unchanged,
 * preserving identity and stack trace. Any other value is wrapped in a new
 * `Error` whose message is recovered via `getErrorMessage`, so a plain-object
 * rejection that carries a real `.message` (e.g. a `@supabase/postgrest-js`
 * fetch-level failure, shaped `{ message, details, hint, code }`, which is NOT
 * an `Error` instance) keeps that message instead of collapsing to
 * `String(value)` (`"[object Object]"`). Values with no recoverable message
 * (numbers, `null`, message-less objects) fall back to `String(value)` as before.
 *
 * Use this in `catch (error)` blocks where downstream code (e.g. structured
 * loggers) expects an `Error` rather than `unknown`.
 *
 * @param error - The caught value to normalize
 * @returns The original `Error`, or a new `Error` wrapping the value
 *
 * @example
 * ```ts
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   logger.error('Operation failed', { error: toErrorInstance(error) });
 * }
 * ```
 */
export function toErrorInstance(error: unknown): Error {
  return error instanceof Error ? error : new Error(getErrorMessage(error, String(error)));
}
